import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiEye, FiHeart, FiCheckCircle } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Passion',
      description: 'Nous investissons toute notre énergie dans la réussite de nos clients, animés par la conviction que l\'excellence est accessible à tous.'
    },
    {
      icon: FiCheckCircle,
      title: 'Intégrité',
      description: 'Nos recommandations sont fondées sur une analyse rigoureuse et indépendante, guidées par l\'éthique professionnelle et la transparence absolue.'
    },
    {
      icon: FiHeart,
      title: 'Bienveillance',
      description: 'Nous adoptons une approche humaniste du conseil, respectueux des cultures locales et soucieux du bien-être de l\'ensemble des parties prenantes.'
    }
  ];

  const principles = [
    'Écoute active et diagnostic approfondi',
    'Adaptation contextuelle aux réalités africaines',
    'Implication et appropriation des collaborateurs',
    'Suivi et amélioration continue'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-16 lg:pt-20"
    >
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-700 dark:from-blue-950 dark:to-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              À Propos de M.A.M Consulting
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Votre partenaire de confiance en développement de compétences et maîtrise des risques
            </p>
          </motion.div>
        </div>
      </section>

      {/* Presentation */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-6">
                Présentation Générale
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                M.A.M Consulting est un cabinet de conseil, de formation et d'audit spécialisé dans l'accompagnement des organisations camerounaises et africaines vers l'excellence opérationnelle.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                Basée à Douala, notre entreprise combine expertise locale et savoir-faire international pour offrir des solutions intégrées en matière de développement des compétences et de maîtrise des risques qualité, sécurité et environnement.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Depuis sa création en 2024, M.A.M Consulting s'affirme comme un acteur majeur du consulting en Afrique centrale, œuvrant pour la transformation durable des organisations par l'amélioration continue et la gestion proactive des risques.
              </p>
            </motion.div>

            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center w-full h-full flex items-center justify-center">
                  <img
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1768751820608-logo%20MAM.jpg"
                    alt="M.A.M Consulting"
                    className="max-h-64 max-w-full object-contain rounded-xl shadow-2xl"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiEye} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white">Notre Vision</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-lg">
                Devenir une référence incontournable dans le développement des compétences et la maîtrise intégrée des risques qualité, sécurité, environnement et gouvernance en Afrique.
              </p>
            </motion.div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-blue-500 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiTarget} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white">Notre Mission</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-lg">
                Accompagner nos clients dans leur parcours de transformation en développant les compétences de leurs collaborateurs et en les soutenant dans la maîtrise efficace de leurs risques qualité, sécurité, environnement et conformité réglementaire.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Nos Valeurs Fondamentales
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-xl text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{value.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 dark:from-blue-900 dark:to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Notre Approche Méthodologique
            </h2>
            <p className="text-xl text-blue-100">
              Quatre principes fondamentaux guident notre intervention
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <motion.div
                key={index}
                initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-4"
              >
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">{index + 1}</span>
                </div>
                <p className="text-white text-lg">{principle}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;